/**
 * Pure deterministic creative sampling — look / motion / stickers / transitions.
 *
 * Time model (do not conflate):
 * - `sourceTick` — master source timeline (cuts omit ranges; stickers/SFX/captions/motion keyed here)
 * - `editTick` — continuous retained output timeline after cut/trim/reorder
 *
 * Transition convention:
 * - Boundary = `editEndTick` of the outgoing beat (start of the next retained segment).
 * - Overlay occupies integer output ticks `[boundary, boundary + durationTicks)`.
 * - `phase = (editTick - boundary) / durationTicks` in [0, 1).
 * - Hard Cut / clear / omitted / final-beat transitions → no overlay.
 * - Scrub/renderAtTime at the same editTick yields the same phase.
 */
import {
  type CameraMotionPreset,
  type LookTreatment,
  type ResolvedEdit,
  type SourcePack,
  type StickerOption,
  type TransitionPreset,
} from "@desk/core";
import {
  buildEditedTimeline,
  type EditedTimeline,
} from "./timeline.js";
import {
  resolvePreviewCaption,
  type PreviewCaptionState,
} from "./caption-preview.js";
import { beatAtTick, buildBeatRanges, lerp } from "./creative-sample-shared.js";
import { stickerArtSrc } from "./sticker-art.js";

/** Neutral look — exact source grade (Clean / cleared / omitted). */
export const NEUTRAL_LOOK: CreativeLookState = {
  treatmentId: null,
  intensityMilli: 0,
  contrast: 1,
  saturation: 1,
  warmth: 1,
  vignette: 0,
};

export interface CreativeLookState {
  treatmentId: string | null;
  intensityMilli: number;
  /** Multipliers / adds applied on top of mood lighting (1 / 0 = identity). */
  contrast: number;
  saturation: number;
  warmth: number;
  vignette: number;
}

export interface CreativeMotionState {
  presetId: string | null;
  /** Absolute pan offsets composed onto authored camera (no cumulative drift). */
  panX: number;
  panY: number;
  /** Multiplier on camera radius / distance (1 = unchanged). */
  zoomMul: number;
  /** FOV delta in degrees (0 = unchanged). */
  fovDelta: number;
}

export type ProceduralStickerShape =
  | "badge"
  | "burst"
  | "arrow"
  | "underline"
  | "frame"
  | "dot"
  | "star"
  | "text";

export interface CreativeStickerInstance {
  /** Instance id (= `op.id`); unlimited instances, unique per resolved.stickers entry. */
  id: string;
  shape: ProceduralStickerShape;
  text: string;
  colorTag: string;
  /** Normalized 0–1 anchors inside the safe reel frame. */
  x: number;
  y: number;
  scale: number;
  rotationDeg: number;
  /** Paint order: first-seen id order in resolved.stickers, index + 1. */
  zIndex: number;
  /** Shell-only curated art override (Fluent Emoji / functional SVG). When
   *  set, presentation renders this image instead of the procedural shape. */
  artSrc?: string;
}

/**
 * Transition overlay at an output boundary.
 * `kind: "none"` means no visible overlay (hard cut, clear, outside window).
 */
export interface CreativeTransitionState {
  kind: "none" | "flash" | "swipe";
  presetId: string | null;
  /** 0 ≤ phase < 1 while active. */
  phase: number;
  durationTicks: number;
  /** Outgoing beat id when active. */
  beatId: string | null;
  /** Output tick of the boundary. */
  boundaryEditTick: number | null;
}

export interface CreativeOverlayModel {
  stickers: CreativeStickerInstance[];
  transition: CreativeTransitionState;
  caption: PreviewCaptionState;
}

export interface CreativeRenderState {
  editTick: number;
  sourceTick: number;
  beatId: string;
  look: CreativeLookState;
  motion: CreativeMotionState;
  overlay: CreativeOverlayModel;
}

export interface SampleCreativeRenderArgs {
  pack: SourcePack;
  resolved: ResolvedEdit;
  /** Integer edited/output tick (clamped by caller or here). */
  editTick: number;
  /** Integer source tick corresponding to editTick. */
  sourceTick: number;
  /** EditGraph seed — only integer catalog/tick/seed inputs (no Date/random). */
  seed: number;
  timeline?: EditedTimeline;
}

const COLOR_FALLBACK = "coral";

export function sampleCreativeRenderState(
  args: SampleCreativeRenderArgs,
): CreativeRenderState {
  const timeline = args.timeline ?? buildEditedTimeline(args.pack, args.resolved);
  const editTick =
    timeline.durationTicks <= 0
      ? 0
      : Math.max(0, Math.min(Math.floor(args.editTick), timeline.durationTicks - 1));
  const sourceTick = Math.max(0, Math.floor(args.sourceTick));
  const ranges = buildBeatRanges(args.pack);
  const beat = beatAtTick(sourceTick, ranges);

  const look = sampleLook(args.pack, args.resolved);
  const motion = sampleMotion(
    args.pack,
    args.resolved,
    sourceTick,
    beat.id,
    args.seed,
  );
  const stickers = sampleStickers(args.pack, args.resolved, sourceTick);
  const transition = sampleTransition(
    args.pack,
    args.resolved,
    timeline,
    editTick,
  );
  const caption = resolvePreviewCaption(
    args.pack,
    args.resolved.captions,
    sourceTick,
  );

  return {
    editTick,
    sourceTick,
    beatId: beat.id,
    look,
    motion,
    overlay: { stickers, transition, caption },
  };
}

export function sampleLook(
  pack: SourcePack,
  resolved: ResolvedEdit,
): CreativeLookState {
  const op = resolved.look;
  if (!op?.treatmentId) return { ...NEUTRAL_LOOK };
  const treatment = (pack.lookTreatments ?? []).find((t) => t.id === op.treatmentId);
  if (!treatment) return { ...NEUTRAL_LOOK };
  return lookFromTreatment(treatment, op.intensityMilli);
}

export function lookFromTreatment(
  treatment: LookTreatment,
  intensityMilli: number | undefined,
): CreativeLookState {
  if (treatment.id === "look-clean" || treatment.tags.includes("clean")) {
    return {
      treatmentId: treatment.id,
      intensityMilli: treatment.defaultIntensityMilli,
      contrast: 1,
      saturation: 1,
      warmth: 1,
      vignette: 0,
    };
  }
  const intensity = treatment.intensitySupported
    ? clamp01((intensityMilli ?? treatment.defaultIntensityMilli) / 1000)
    : 1;
  return {
    treatmentId: treatment.id,
    intensityMilli: Math.round(intensity * 1000),
    contrast: lerp(1, treatment.contrastMilli / 1000, intensity),
    saturation: lerp(1, treatment.saturationMilli / 1000, intensity),
    warmth: lerp(1, treatment.warmthMilli / 1000, intensity),
    vignette: lerp(0, treatment.vignetteMilli / 1000, intensity),
  };
}

export function sampleMotion(
  pack: SourcePack,
  resolved: ResolvedEdit,
  sourceTick: number,
  beatId: string,
  seed: number,
): CreativeMotionState {
  const identity: CreativeMotionState = {
    presetId: null,
    panX: 0,
    panY: 0,
    zoomMul: 1,
    fovDelta: 0,
  };
  const op = resolved.motions.get(beatId);
  if (!op?.presetId) return identity;
  const preset = (pack.cameraMotions ?? []).find((m) => m.id === op.presetId);
  if (!preset) return identity;
  if (
    preset.id === "motion-steady" ||
    preset.tags.includes("steady") ||
    (preset.amplitudeMilli === 0 &&
      preset.frequencyMilli === 0 &&
      preset.zoomDeltaMilli === 0)
  ) {
    return { ...identity, presetId: preset.id };
  }

  const beat = pack.beats.find((b) => b.id === beatId);
  const beatStart = beat?.startTick ?? 0;
  const beatEnd = beat?.endTick ?? beatStart + 1;
  const beatLen = Math.max(1, beatEnd - beatStart);
  const tickInBeat = Math.max(0, Math.floor(sourceTick) - beatStart);
  const beatT = tickInBeat / beatLen;
  const tick = Math.floor(sourceTick);

  return motionFromPreset(preset, {
    beatT,
    tick,
    tickInBeat,
    seed,
    beatId,
  });
}

export function motionFromPreset(
  preset: CameraMotionPreset,
  ctx: {
    beatT: number;
    tick: number;
    tickInBeat: number;
    seed: number;
    beatId: string;
  },
): CreativeMotionState {
  const amp = preset.amplitudeMilli / 1000;
  const freq = preset.frequencyMilli / 1000;
  const zoomAmt = preset.zoomDeltaMilli / 1000;
  const seedMix = hashInt(ctx.seed, ctx.beatId);

  if (preset.id === "motion-push-in" || preset.tags.includes("push")) {
    const t = clamp01(ctx.beatT);
    return {
      presetId: preset.id,
      panX: 0,
      panY: amp * 0.04 * t,
      zoomMul: 1 + zoomAmt * t,
      fovDelta: -3.5 * zoomAmt * t,
    };
  }

  if (preset.id === "motion-handheld" || preset.tags.includes("handheld")) {
    const phase = (ctx.tick * freq * 0.35 + seedMix * 0.01) % 1024;
    const panX = Math.sin(phase * 1.7) * amp * 0.085;
    const panY = Math.cos(phase * 1.3 + 0.6) * amp * 0.07;
    const zoomMul = 1 + Math.sin(phase * 0.9) * zoomAmt * 0.12;
    return {
      presetId: preset.id,
      panX,
      panY,
      zoomMul,
      fovDelta: Math.sin(phase * 1.1) * zoomAmt * 1.2,
    };
  }

  if (preset.id === "motion-snap-zoom" || preset.tags.includes("snap")) {
    const period = Math.max(2, Math.round(8 / Math.max(0.15, freq)));
    const local = ctx.tickInBeat % period;
    const snapT = local / period;
    // Sharp in, ease out — deterministic pulse within the beat.
    const pulse = snapT < 0.22 ? snapT / 0.22 : 1 - (snapT - 0.22) / 0.78;
    const shaped = pulse * pulse;
    return {
      presetId: preset.id,
      panX: Math.sin((seedMix + ctx.tickInBeat) * 0.4) * amp * 0.03 * shaped,
      panY: 0,
      zoomMul: 1 + zoomAmt * shaped,
      fovDelta: -5 * zoomAmt * shaped,
    };
  }

  // Unknown tagged preset: mild amplitude-driven pan (still deterministic).
  const phase = (ctx.tick * Math.max(0.05, freq) + seedMix * 0.02) % 512;
  return {
    presetId: preset.id,
    panX: Math.sin(phase) * amp * 0.05,
    panY: Math.cos(phase * 0.8) * amp * 0.04,
    zoomMul: 1 + zoomAmt * 0.25 * clamp01(ctx.beatT),
    fovDelta: 0,
  };
}

export function sampleStickers(
  pack: SourcePack,
  resolved: ResolvedEdit,
  sourceTick: number,
): CreativeStickerInstance[] {
  const byId = new Map((pack.stickerOptions ?? []).map((s) => [s.id, s]));
  const out: CreativeStickerInstance[] = [];
  // resolved.stickers is already ordered by first-seen id (paint order) —
  // iterate the full unlimited list directly (NOT a fixed 2-slot subset).
  resolved.stickers.forEach((op, index) => {
    if (sourceTick < op.startTick || sourceTick >= op.endTick) return;
    const option = byId.get(op.stickerId);
    if (!option) return;
    out.push(stickerInstanceFromOp(op.id, op, option, index));
  });
  return out;
}

function stickerInstanceFromOp(
  id: string,
  op: {
    anchorXMilli: number;
    anchorYMilli: number;
    scaleMilli: number;
    rotationMilli: number;
  },
  option: StickerOption,
  index: number,
): CreativeStickerInstance {
  const render = option.render;
  const shape: ProceduralStickerShape =
    render.kind === "procedural" ? render.shape : "badge";
  const text =
    render.kind === "procedural"
      ? (render.text ?? (shape === "text" ? option.label : "")).slice(0, 12)
      : option.label.slice(0, 12);
  const colorTag =
    render.kind === "procedural" ? render.colorTag || COLOR_FALLBACK : COLOR_FALLBACK;
  // Keep anchors inside a soft safe area (6%–94%).
  const x = clamp(op.anchorXMilli / 1000, 0.06, 0.94);
  const y = clamp(op.anchorYMilli / 1000, 0.06, 0.94);
  return {
    id,
    shape,
    text,
    colorTag,
    x,
    y,
    scale: Math.max(0.35, Math.min(2.2, op.scaleMilli / 1000)),
    rotationDeg: (op.rotationMilli / 1000) % 360,
    zIndex: index + 1,
    artSrc: stickerArtSrc(option.id),
  };
}

export function sampleTransition(
  pack: SourcePack,
  resolved: ResolvedEdit,
  timeline: EditedTimeline,
  editTick: number,
): CreativeTransitionState {
  const none: CreativeTransitionState = {
    kind: "none",
    presetId: null,
    phase: 0,
    durationTicks: 0,
    beatId: null,
    boundaryEditTick: null,
  };
  if (timeline.segments.length < 2) return none;

  for (let i = 0; i < timeline.segments.length - 1; i++) {
    const outgoing = timeline.segments[i]!;
    const incoming = timeline.segments[i + 1]!;
    const boundary = outgoing.editEndTick;
    // Cap at the incoming segment end so a long transition cannot leak past
    // the next edited boundary / following transition.
    const hardEnd = incoming.editEndTick;
    const op = resolved.transitions.get(outgoing.beatId);
    if (!op?.presetId) continue;
    const preset = (pack.transitionPresets ?? []).find((t) => t.id === op.presetId);
    if (!preset) continue;
    const authored = Math.max(1, Math.min(op.durationTicks, preset.maxDurationTicks));
    const cappedDuration = Math.max(1, Math.min(authored, hardEnd - boundary));
    if (editTick < boundary || editTick >= boundary + cappedDuration) continue;
    return transitionStateFromPreset(preset, {
      phase: (editTick - boundary) / cappedDuration,
      durationTicks: cappedDuration,
      beatId: outgoing.beatId,
      boundaryEditTick: boundary,
    });
  }
  return none;
}

export function transitionStateFromPreset(
  preset: TransitionPreset,
  ctx: {
    phase: number;
    durationTicks: number;
    beatId: string;
    boundaryEditTick: number;
  },
): CreativeTransitionState {
  if (
    preset.id === "transition-hard-cut" ||
    preset.tags.includes("cut") ||
    preset.tags.includes("clean")
  ) {
    return {
      kind: "none",
      presetId: preset.id,
      phase: 0,
      durationTicks: ctx.durationTicks,
      beatId: ctx.beatId,
      boundaryEditTick: ctx.boundaryEditTick,
    };
  }
  const kind: "flash" | "swipe" =
    preset.id === "transition-swipe" || preset.tags.includes("swipe")
      ? "swipe"
      : "flash";
  return {
    kind,
    presetId: preset.id,
    phase: clamp01(ctx.phase),
    durationTicks: ctx.durationTicks,
    beatId: ctx.beatId,
    boundaryEditTick: ctx.boundaryEditTick,
  };
}

/** Map SFX source atTick → edited tick; null when the source moment is omitted. */
export function mapSfxSourceTickToEditTick(
  timeline: EditedTimeline,
  sourceAtTick: number,
): number | null {
  for (const seg of timeline.segments) {
    if (sourceAtTick >= seg.sourceStartTick && sourceAtTick < seg.sourceEndTick) {
      return seg.editStartTick + (sourceAtTick - seg.sourceStartTick);
    }
  }
  return null;
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/** Stable integer mix from seed + beat id (no Math.random). */
function hashInt(seed: number, beatId: string): number {
  let h = (seed | 0) ^ 0x9e3779b9;
  for (let i = 0; i < beatId.length; i++) {
    h = Math.imul(h ^ beatId.charCodeAt(i), 0x5bd1e995);
    h = (h << 13) | (h >>> 19);
  }
  return h >>> 0;
}
